import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import HealthCirclesLogo from "@/components/HealthCirclesLogo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      // Staff members land straight on the patient-journey worklist.
      if (u?.role === "staff") nav("/journey");
      else nav("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend returns a structured detail when OTP verification is pending.
      if (detail && typeof detail === "object" && detail.code === "otp_required") {
        toast.message("Please verify your email to continue");
        nav(`/verify-otp?email=${encodeURIComponent(detail.email || email)}`);
        return;
      }
      toast.error((typeof detail === "string" && detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left brand column */}
      <div className="hidden md:flex relative flex-col justify-between p-12 text-white overflow-hidden card-hero-emerald rounded-none">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center">
            <HealthCirclesLogo size={22} />
          </div>
          <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: "Instrument Sans" }}>HealthCircles</div>
        </div>
        <div className="max-w-md">
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight leading-tight" style={{ fontFamily: "Instrument Sans" }}>
            Two minutes on the screen.<br /> The rest with your patient.
          </h1>
          <p className="mt-5 text-white/80 text-base leading-relaxed">
            A calm, fast EMR built for doctors. Manage patients, appointments and prescriptions
            without the clinical software fatigue.
          </p>
        </div>
        <div className="text-white/60 text-sm">© {new Date().getFullYear()} HealthCircles</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 md:p-16 bg-[#FDFBF7]">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8">
            <div className="field-label mb-3">Welcome back</div>
            <h2 className="text-3xl font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Sign in to HealthCircles</h2>
            <p className="mt-2 text-sm text-[#6C7B72]">Continue caring for your patients.</p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="field-label block mb-2">Email</label>
              <input data-testid="login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-clean" placeholder="doctor@clinic.com" />
            </div>
            <div>
              <label className="field-label block mb-2">Password</label>
              <input data-testid="login-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-clean" placeholder="••••••••" />
            </div>
            <button data-testid="login-submit" disabled={busy} className="btn-emerald w-full mt-2">
              {busy ? "Signing in…" : (<>Sign in <ArrowRight size={16} /></>)}
            </button>
          </div>
          <div className="mt-4 text-sm">
            <Link data-testid="link-forgot" to="/forgot-password" className="text-[#0A4C36] hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="mt-6 text-sm text-[#6C7B72]">
            New to HealthCircles?{" "}
            <Link data-testid="link-register" to="/register" className="text-[#0A4C36] font-semibold hover:underline">Create an account</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
